/**
 * The single consent interface. Everything outside this directory uses only what
 * is exported here — no consumer reads a cookie, touches `window.OneTrust`, or
 * knows which provider answered.
 */
import { getPublicOneTrustEnabled } from "@/config/public-runtime-config";

import { ConsentGroup, type ConsentState } from "./groups";
import { readHomegrownConsent, writeHomegrownConsent } from "./homegrown";
import { openOneTrustPreferences, readOneTrustConsent, subscribeOneTrust } from "./onetrust";

export {
  ALL_CONSENT_MODE_SIGNALS,
  ALL_GROUPS,
  CONSENT_GROUP_INFO,
  ConsentGroup,
  GTM_GROUPS,
  consentModeState,
  type ConsentGroupInfo,
  type ConsentModeSignal,
  type ConsentModeValue,
  type ConsentState,
} from "./groups";
export { CONSENT_COOKIE, CONSENT_MAX_AGE_DAYS } from "./homegrown";

export type ConsentProvider = "onetrust" | "homegrown";

/**
 * OneTrust owns consent wherever it's configured; our banner stands down there.
 * Driven by the same validated domainScript that decides whether the OneTrust
 * stub is injected server-side, so we can never stand down waiting for a CMP
 * that was never loaded.
 */
export function getConsentProvider(): ConsentProvider {
  return getPublicOneTrustEnabled() ? "onetrust" : "homegrown";
}

export function readConsent(): ConsentState {
  return getConsentProvider() === "onetrust" ? readOneTrustConsent() : readHomegrownConsent();
}

/**
 * Necessary cookies need no decision. Everything else requires an explicit one,
 * so a pre-populated OneTrust default or a corrupt cookie grants nothing.
 */
export function hasConsent(group: ConsentGroup): boolean {
  if (group === ConsentGroup.Necessary) return true;
  const { decided, granted } = readConsent();
  return decided && granted.has(group);
}

const listeners = new Set<(state: ConsentState) => void>();
let providerSubscribed = false;
let openOwnPanel: (() => void) | null = null;

function notify(): void {
  const state = readConsent();
  for (const listener of listeners) listener(state);
}

/**
 * Notified on every consent change, whoever made it — our panel or OneTrust's.
 * The provider subscription is attached once, on first use, and left for the
 * page lifetime; there's nothing to reclaim and refcounting it would only add a
 * way to unsubscribe too early.
 */
export function subscribeConsent(listener: (state: ConsentState) => void): () => void {
  listeners.add(listener);
  if (!providerSubscribed && getConsentProvider() === "onetrust") {
    providerSubscribed = true;
    subscribeOneTrust(notify);
  }
  return () => {
    listeners.delete(listener);
  };
}

/**
 * No-op under OneTrust: the CMP owns its own record, and callers that want to
 * change consent should use `openConsentPreferences`, which is correct for both
 * providers. Throwing here would just push provider checks back into consumers.
 */
export function saveConsent(groups: Iterable<ConsentGroup>): void {
  if (getConsentProvider() === "onetrust") return;
  writeHomegrownConsent(groups);
  notify();
}

/** Registered by the consent store so our own panel can be reopened. */
export function setConsentPanelOpener(open: () => void): void {
  openOwnPanel = open;
}

/** Provider-correct re-entry point — wire a footer "Cookie preferences" link to this. */
export function openConsentPreferences(): void {
  if (getConsentProvider() === "onetrust") {
    openOneTrustPreferences();
    return;
  }
  openOwnPanel?.();
}
